<template>
  <date-picker
      v-model="innerValue"
      ref="datepicker"
      :mode="currentMode"
      :input-debounce="0"
      :model-config="modelConfig"
      locale="ru"
      :masks="{
        weekdays: 'WW',
      }"
      :trim-weeks="true"
      :min-date="minDateLimit"
      :max-date="maxDate"
      :popover="popoverConfig"
      :minute-increment="minuteIncrement"
      is24hr
      @popover-will-show="$emit('popover-will-show')"
      @popover-will-hide="$emit('popover-will-hide')"
      class="ui-datepicker"
  >
    <template #header-left-button>
      <ui-icon
          name="rightArrow"
          :size="16"
          rotate
      />
    </template>
    <template #header-right-button>
      <ui-icon
          name="rightArrow"
          :size="16"
      />
    </template>
    <template v-if="hasInputSlot" v-slot="{ inputValue, inputEvents }">
      <slot name="input" :inputValue="inputValue" :input-events="inputEvents"/>
    </template>
  </date-picker>
</template>

<script>
import { DatePicker } from 'v-calendar/lib/v-calendar.umd'
import { dateFormat, dateFormats } from '../../libs/date'
import UiIcon from '../icons/UiIcon.vue'

export default {
  components: { UiIcon, DatePicker },
  props: {
    value: {
      type: [String, Number, Date],
      default: ''
    },
    mode: {
      type: String,
      required: false
    },
    minDate: [String, Number, Date],
    maxDate: [String, Number, Date],
    isFutureDate: {
      type: Boolean,
      default: false
    },
    format: {
      type: String,
      required: false
    },
    hasTime: {
      type: Boolean,
      default: false
    },
    onlyTime: {
      type: Boolean,
      default: false
    },
    minuteIncrement: {
      type: Number,
      required: false
    },
    hasInputSlot: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    innerValue: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
      }
    },
    modelConfig () {
      return {
        type: 'string',
        mask: this.currentFormat
      }
    },
    popoverConfig () {
      return {
        showDelay: 50
      }
    },
    currentMode () {
      if (this.mode) return this.mode

      if (this.hasTime) {
        return 'dateTime'
      }

      if (this.onlyTime) {
        return 'time'
      }

      return 'date'
    },
    currentFormat () {
      return (this.format ??
          dateFormats[this.currentMode] ??
          dateFormats.date)
    },
    minDateLimit () {
      return this.isFutureDate ? Date.now() : this.minDate
    },
    formatDate () {
      return dateFormat(this.value, dateFormats.dateRu)
    }
  },

  methods: {
    show () {
      this.$refs.datepicker.showPopover()
      this.$emit('focus')
    },
    hide () {
      this.$refs.datepicker.hidePopover()
      this.$emit('blur')
    },
    updateValue (date) {
      this.$refs.datepicker.updateValue(date)
    }
  }
}
</script>

<style lang="scss" scoped>
.ui-datepicker ::v-deep() {
  font-family: $main-ff;
  border: 1px solid $gray-5;
  border-radius: $border-radius-00;

  .vc-header {
    padding-top: 30px;
  }

  .vc-arrows-container {
    padding: 28px;
  }

  .vc-arrow {
    border-width: 0;
    fill: $gray-1;

    &:hover {
      fill: $primary;
      background-color: initial;
    }
  }

  .vc-weeks {
    padding: 24px 20px 0 20px;
  }

  .vc-pane {
    min-width: 290px;
  }

  .vc-title {
    @include font-14;

    font-weight: bold;
    text-transform: capitalize;
    line-height: 150%;
    color: $gray-1;

    &:hover {
      color: $primary;
      opacity: 1;
    }
  }

  .vc-weekday {
    @include font-12;

    font-weight: 400;
    color: $gray-4;
    text-transform: uppercase;
  }

  .vc-day-content {
    font-weight: 400;
    color: $gray-1;

    &:hover {
      color: $primary;
      background-color: initial;
    }

    &:focus {
      background-color: initial;
    }
  }

  .vc-day {
    width: 36px;
    height: 36px;

    &.on-bottom {
      margin-bottom: 20px;
    }

    &.is-not-in-month .vc-day-content {
      color: $gray-4;
      opacity: 1;
    }

    &.is-today .vc-day-content {
      color: $primary;
    }
  }

  .vc-highlight {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: $primary !important;
  }
}
</style>
